import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faImage,
  faChevronLeft,
  faChevronRight,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import "../../../styles/pages/gallery.css";
// Importe za slike klasičnih trepavica
import klasicne1 from "../../../images/gallery/klasicne1.jpg";
import klasicne2 from "../../../images/gallery/klasicne2.jpg";
import klasicne3 from "../../../images/gallery/klasicne3.jpg";
import klasicne4 from "../../../images/gallery/klasicne4.jpg";
import klasicne5 from "../../../images/gallery/klasicne5.jpg";
import klasicne6 from "../../../images/gallery/klasicne6.jpg";

// Importe za slike volumenskih trepavica
import volumenske1 from "../../../images/gallery/volumenske1.jpg";
import volumenske2 from "../../../images/gallery/volumenske2.jpg";
import volumenske3 from "../../../images/gallery/volumenske3.jpg";
import volumenske4 from "../../../images/gallery/volumenske4.jpg";
import volumenske5 from "../../../images/gallery/volumenske5.jpg";
import volumenske6 from "../../../images/gallery/volumenske6.jpg";
import volumenske7 from "../../../images/gallery/volumenske7.jpg";
import volumenske8 from "../../../images/gallery/volumenske8.jpg";

// Definiranje sučelja za kategoriju galerije
interface GalleryCategory {
  id: string;
  title: string;
  images: string[];
}

const Gallery: React.FC = () => {
  // Podaci o kategorijama i slikama
  const galleryData: GalleryCategory[] = [
    {
      id: "klasicne",
      title: "Klasične trepavice",
      images: [
        klasicne1,
        klasicne2,
        klasicne3,
        klasicne4,
        klasicne5,
        klasicne6,
      ],
    },
    {
      id: "volumenske",
      title: "Volumenske trepavice",
      images: [
        volumenske1,
        volumenske2,
        volumenske3,
        volumenske4,
        volumenske5,
        volumenske6,
        volumenske7,
        volumenske8,
      ],
    },
  ];

  const [activeCategory, setActiveCategory] = useState<string>("klasicne");
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [imagesLoaded, setImagesLoaded] = useState<{ [key: string]: boolean }>(
    {}
  );

  // Učitavanje slika
  useEffect(() => {
    // Simulacija vremena učitavanja
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  // Pratimo učitavanje svake slike
  const handleImageLoaded = (imageUrl: string) => {
    setImagesLoaded((prev) => ({
      ...prev,
      [imageUrl]: true,
    }));
  };

  // Otvaranje lightboxa za pregled slike
  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden"; // Onemogući scrollanje dok je lightbox otvoren
  };

  // Zatvaranje lightboxa
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "auto"; // Vrati scrollanje
  };

  // Kretanje kroz slike u lightboxu
  const navigateImage = (direction: "prev" | "next") => {
    const currentCategory = galleryData.find(
      (cat) => cat.id === activeCategory
    );
    if (!currentCategory) return;

    const totalImages = currentCategory.images.length;

    if (direction === "prev") {
      setCurrentImageIndex((prev) => (prev === 0 ? totalImages - 1 : prev - 1));
    } else {
      setCurrentImageIndex((prev) => (prev === totalImages - 1 ? 0 : prev + 1));
    }
  };

  // Prikaz različitih kategorija
  const changeCategory = (categoryId: string) => {
    setActiveCategory(categoryId);
  };

  // Određuje trenutno aktivnu kategoriju
  const getCurrentCategory = (): GalleryCategory | undefined => {
    return galleryData.find((category) => category.id === activeCategory);
  };

  // Rukovanje pritiscima tipki u lightboxu
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;

      switch (e.key) {
        case "Escape":
          closeLightbox();
          break;
        case "ArrowLeft":
          navigateImage("prev");
          break;
        case "ArrowRight":
          navigateImage("next");
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [lightboxOpen]);

  return (
    <div className="gallery-page">
      {/* Hero sekcija */}
      <section className="gallery-hero">
        <div className="container">
          <div className="gallery-hero-content">
            <h1 className="gallery-hero-title">Galerija radova</h1>
            <p className="gallery-hero-description">
              Pogledajte naše transformacije i primjere radova. Ovdje možete
              vidjeti rezultate našeg stručnog pristupa i kvalitetnih tretmana.
            </p>
          </div>
        </div>
      </section>

      {/* Galerija sekcija */}
      <section className="gallery-section">
        <div className="container">
          {/* Izbornik kategorija */}
          <div className="gallery-categories">
            {galleryData.map((category) => (
              <button
                key={category.id}
                className={`category-btn ${
                  activeCategory === category.id ? "active" : ""
                }`}
                onClick={() => changeCategory(category.id)}
              >
                <FontAwesomeIcon icon={faImage} />
                <span>{category.title}</span>
                <span className="image-count">{category.images.length}</span>
              </button>
            ))}
          </div>

          {/* Prikaz slika */}
          <div className="gallery-container">
            {isLoading ? (
              <div className="gallery-loading">
                <div className="spinner"></div>
                <p>Učitavanje galerije...</p>
              </div>
            ) : (
              <div className="gallery-grid">
                {getCurrentCategory()?.images.map((image, index) => (
                  <div
                    key={image}
                    className={`gallery-item ${
                      imagesLoaded[image] ? "loaded" : ""
                    }`}
                    onClick={() => openLightbox(index)}
                  >
                    <div className="gallery-item-inner">
                      <img
                        src={image}
                        alt={`${getCurrentCategory()?.title} - slika ${
                          index + 1
                        }`}
                        onLoad={() => handleImageLoaded(image)}
                      />
                      <div className="gallery-item-overlay">
                        <FontAwesomeIcon icon={faImage} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Lightbox za pregled slika */}
            {lightboxOpen && (
              <div className="lightbox">
                <div className="lightbox-overlay" onClick={closeLightbox}></div>
                <div className="lightbox-content">
                  <button
                    className="lightbox-close"
                    onClick={closeLightbox}
                    aria-label="Zatvori"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>

                  <div className="lightbox-image-container">
                    <img
                      src={getCurrentCategory()?.images[currentImageIndex]}
                      alt={`${getCurrentCategory()?.title} - slika ${
                        currentImageIndex + 1
                      }`}
                      className="lightbox-image"
                    />
                  </div>

                  <button
                    className="lightbox-nav lightbox-prev"
                    onClick={() => navigateImage("prev")}
                    aria-label="Prethodna slika"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>

                  <button
                    className="lightbox-nav lightbox-next"
                    onClick={() => navigateImage("next")}
                    aria-label="Sljedeća slika"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>

                  <div className="lightbox-caption">
                    <span className="caption-title">
                      {getCurrentCategory()?.title}
                    </span>
                    <span className="caption-counter">
                      {currentImageIndex + 1} /{" "}
                      {getCurrentCategory()?.images.length}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
